<!-- Sidebar Navigation Component -->
<div class="sidebar-wrapper" [class.collapsed]="isSidebarCollapsed">
  <!-- Sidebar Header Section -->
  <div class="sidebar-top">
    <!-- Company Logo and Name -->
    <div class="company-name-wrapper" [class.collapsed]="isSidebarCollapsed">
      <div class="company-logo">
        <img src="../../../assets/images/company-logo.svg" alt="Company Logo" />
      </div>
      @if (!isSidebarCollapsed) {
      <div class="company-name">
        <span class="logo-text">{{ 'ui_app_lbl' | translate }}</span>
      </div>
      }
    </div>

    <!-- Shop Selection Dropdown (visible when expanded) -->
    @if (!isSidebarCollapsed) {
    <div class="shop-selection-block">
      <div class="shop-block">
        <ng-select
          class="shop-dropdown"
          [(ngModel)]="activeShopId"
          (ngModelChange)="changeShop($event)"
          [clearable]="false"
          [virtualScroll]="true"
          [notFoundText]="'ui_no_items_found' | translate"
          bindLabel="title"
          appendTo="body"
          (scrollToEnd)="onScrollToEnd($event)"
          aria-label="Select Shop"
        >
          @for (shop of shopList; track shop._id) {
          <ng-option [value]="shop._id">{{ shop.businessName }}</ng-option>
          }
        </ng-select>
      </div>
    </div>
    }
  </div>

  <!-- Glassmorphic wrapper for incomplete setup -->
  @if (isGlassmorphicEnabled) {
  <div class="sidebar-glass-wrapper">
    <!-- Setup incomplete banner -->
    <div
      class="sidebar-top-banner"
      (click)="navigateToRoute()"
      role="button"
      tabindex="0"
      (keydown.enter)="navigateToRoute()"
      (keydown.space)="navigateToRoute()"
      aria-label="Complete setup steps"
    >
      <img src="../../../assets/icons/lock.svg" alt="Lock icon" />
      {{ 'ui_complete_all_steps_lbl' | translate }}
    </div>
    <ng-container [ngTemplateOutlet]="sidebarData"></ng-container>
  </div>
  } @else {
  <ng-container [ngTemplateOutlet]="sidebarData"></ng-container>
  }

  <div class="sidebar-footer" [class.collapsed]="isSidebarCollapsed">
    <span class="version-text">{{ softwareVersion }}</span>
  </div>
</div>

<!-- Sidebar Navigation Template -->
<ng-template #sidebarData>
  <nav class="sidebar-body" [class.collapsed]="isSidebarCollapsed" aria-label="Main navigation">
    @if (moduleList.length) {
    <div class="sidebar-items">
      <!-- Main Module Items -->
      @for (module of moduleList; track module.label; let i = $index) {
      <div
        class="module-container"
        [class.collapsed]="isSidebarCollapsed"
        (mouseenter)="onHover(module, $event)"
        (mouseleave)="onLeave()"
      >
        <div class="sidebar-item-wrapper" [class.collapsed]="isSidebarCollapsed">
          <!-- Main Module Item -->
          <div
            class="sidebar-item"
            role="button"
            tabindex="0"
            [attr.aria-label]="module.displayLabel"
            [attr.aria-expanded]="module.label === activeTabLabel && selectedModule"
            (click)="navigateTo(module, 'item', module)"
            (keydown.enter)="navigateTo(module, 'item', module)"
            (keydown.space)="navigateTo(module, 'item', module)"
            [class.active-module]="module.label === activeTabLabel"
            [class.collapsed]="isSidebarCollapsed"
          >
            <!-- Module Icon -->
            <div class="sidebar-item-icon" [class.collapsed]="isSidebarCollapsed">
              <img
                [src]="module.label === activeTabLabel ? module.activeIcon : module.icon"
                [alt]="module.displayLabel + ' icon'"
                [class.module-icon-active]="module.label === activeTabLabel"
                [class.module-icon]="module.label !== activeTabLabel"
              />
            </div>

            <!-- Module Text and Indicators (visible when expanded) -->
            @if (!isSidebarCollapsed) {
            <div class="sidebar-item-text">
              <div class="item-text-wrapper">
                {{ module.displayLabel }}
                <!-- New Badge -->
                @if (module.isNew && module.label !== activeTabLabel) {
                <span class="new-tag">{{ 'ui_new_lbl' | translate }} ⭐️</span>
                }
              </div>

              <!-- Expand/Collapse Arrow -->
              @if (module.subItems.length) { @if (module.label === activeTabLabel) {
              <!-- Active module arrow -->
              <img
                [src]="
                  selectedModule
                    ? '../../../assets/icons/sidebar/down-arrow-active.svg'
                    : '../../../assets/icons/sidebar/right-arrow-white.svg'
                "
                class="icon-more-options-active"
                alt="Expand/Collapse"
              />
              } @else {
              <!-- Inactive module arrow -->
              <img src="../../../assets/icons/sidebar/right-arrow.svg" class="icon-more-options" alt="Expand" />
              } }
            </div>
            }
          </div>

          <!-- Sub-items (visible when module is selected and expanded) -->
          @if (selectedModule && module.label === selectedModule.label && selectedModule.subItems.length &&
          !isSidebarCollapsed) {
          <div class="sidebar-subitems" role="menu" [attr.aria-label]="module.displayLabel + ' submenu'">
            @for (subItem of selectedModule.subItems; track subItem.link) {
            <div
              class="sidebar-item sub-item"
              role="menuitem"
              tabindex="0"
              [attr.aria-label]="subItem.displayLabel"
              [attr.aria-current]="subItem.link === currentActivePath ? 'page' : null"
              (click)="navigateTo(subItem, 'sub', module)"
              (keydown.enter)="navigateTo(subItem, 'sub', module)"
              (keydown.space)="navigateTo(subItem, 'sub', module)"
              [class.active-sub-module]="subItem.link === currentActivePath"
            >
              <!-- Active indicator dot -->
              <div class="dot" [class.active-dot]="subItem.link === currentActivePath" aria-hidden="true"></div>
              <div class="sidebar-item-text">
                <span>{{ subItem.displayLabel }}</span>
              </div>
            </div>
            }
          </div>
          }
        </div>
      </div>
      }
    </div>
    }

    <!-- Hover card: Sub-items displayed when sidebar is collapsed -->
    @if (hoveredModule && isSidebarCollapsed) {
    <div
      class="sub-items-card"
      role="tooltip"
      [class.visible]="isHovered"
      [ngStyle]="subItemsCardPosition"
      (mouseenter)="onCardHover()"
      (mouseleave)="onCardLeave()"
      [attr.aria-label]="hoveredModule.displayLabel + ' submenu'"
    >
      <!-- Card Header -->
      <div class="sub-items-card-header">
        {{ hoveredModule.displayLabel }}
        @if (hoveredModule.isNew) {
        <span class="card-new-tag" aria-label="New feature">⭐️ {{ 'ui_new_lbl' | translate }}</span>
        }
      </div>

      <!-- Sub-items List -->
      @if (hoveredModule.subItems.length > 0) {
      <!-- Separator Line -->
      <div class="separator-line" aria-hidden="true">
        <img src="../../../assets/svg/seperatorLine.svg" alt="" />
      </div>

      <!-- Sub-item Links -->
      <div class="sub-items-list" role="menu">
        @for (subItem of hoveredModule.subItems; track subItem.link) {
        <div
          class="sidebar-item"
          role="menuitem"
          tabindex="0"
          [attr.aria-label]="subItem.displayLabel"
          [attr.aria-current]="subItem.link === currentActivePath ? 'page' : null"
          (click)="navigateTo(subItem, 'sub', hoveredModule)"
          (keydown.enter)="navigateTo(subItem, 'sub', hoveredModule)"
          (keydown.space)="navigateTo(subItem, 'sub', hoveredModule)"
          [class.active-sub-module]="subItem.link === currentActivePath"
        >
          <div class="dot" [class.active-dot]="subItem.link === currentActivePath" aria-hidden="true"></div>
          <div class="sidebar-item-text">
            <span>{{ subItem.displayLabel }}</span>
          </div>
        </div>
        }
      </div>
      }
    </div>
    }
  </nav>
</ng-template>
